import React, { useState, useEffect } from 'react';
import { projectsAPI } from '../../utils/api';
import ProjectForm from '../../components/admin/ProjectForm';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import '../../styles/AdminMbaProjects.scss';
import '../../styles/AdminProjects.scss';

const AdminProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const projectsPerPage = 20;

  // Filter states
  const [search, setSearch] = useState('');
  const [techDomain, setTechDomain] = useState('');
  const [academicDomain, setAcademicDomain] = useState('');

  // Bulk update states
  const [bulkTechDomain, setBulkTechDomain] = useState('');
  const [bulkOriginalPrice, setBulkOriginalPrice] = useState('');
  const [bulkDiscountedPrice, setBulkDiscountedPrice] = useState('');
  const [bulkIsDiscountActive, setBulkIsDiscountActive] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkSuccess, setBulkSuccess] = useState('');

  const techDomainsList = [
    'AI & Machine Learning',
    'Web Development',
    'Android App Development',
    'Data Science & Data Analytics',
    'IoT & Embedded Systems',
    'Cloud Computing & DevOps',
    'Cyber Security & Ethical Hacking',
    'Blockchain & Cryptography',
    'Desktop Application Development',
    'Game Development',
    'Other'
  ];

  const academicDomainsList = [
    'BCA/BCS/MCA',
    'BE/B.Tech/ME/M.Tech/Diploma'
  ];

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, techDomain, academicDomain, projects.length]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await projectsAPI.getAll();
      const projectsArray = response.data?.data || [];
      setProjects(Array.isArray(projectsArray) ? projectsArray : []);
    } catch (err) {
      setError('Failed to load projects');
      console.error('Error:', err);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = async (formData) => {
    try {
      if (editingProject) {
        await projectsAPI.update(editingProject.id, formData);
      } else {
        await projectsAPI.create(formData);
      }
      
      // Refresh projects list
      await fetchProjects();
      
      // Close form
      setShowForm(false);
      setEditingProject(null);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to save project';
      throw new Error(errorMsg);
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingProject(null);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        await projectsAPI.delete(id);
        setProjects(projects.filter(p => p.id !== id));
      } catch (err) {
        setError('Failed to delete project');
        console.error('Error:', err);
      }
    }
  };

  const handleBulkPriceUpdate = async (e) => {
    e.preventDefault();
    if (!bulkTechDomain) {
      alert('Please select a Technology Domain for bulk update.');
      return;
    }
    const origPrice = Number(bulkOriginalPrice);
    if (isNaN(origPrice) || origPrice < 0) {
      alert('Original Price cannot be negative.');
      return;
    }

    let discPrice = null;
    if (bulkIsDiscountActive) {
      discPrice = Number(bulkDiscountedPrice);
      if (isNaN(discPrice) || discPrice >= origPrice || discPrice < 0) {
        alert('Discounted Price must be valid and less than Original Price.');
        return;
      }
    }

    if (!window.confirm(`Are you sure you want to update prices for ALL projects under Technology Domain "${bulkTechDomain}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setBulkLoading(true);
      setBulkSuccess('');
      setError('');

      const payload = {
        category: bulkTechDomain,
        originalPrice: origPrice,
        discountedPrice: bulkIsDiscountActive ? discPrice : null,
        isDiscountActive: bulkIsDiscountActive
      };

      await projectsAPI.bulkPriceUpdate(payload);
      setBulkSuccess(`Successfully updated all "${bulkTechDomain}" project prices!`);
      await fetchProjects();

      // Reset bulk form
      setBulkOriginalPrice('');
      setBulkDiscountedPrice('');
      setBulkIsDiscountActive(false);
      setBulkTechDomain('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update bulk prices');
    } finally {
      setBulkLoading(false);
    }
  };

  const getFilteredProjects = () => {
    return projects.filter(p => {
      const matchesSearch = search.trim() === '' || p.title.toLowerCase().includes(search.toLowerCase());
      const matchesTech = techDomain === '' || p.category === techDomain;
      const matchesAcademic = academicDomain === '' || p.domain === academicDomain;
      return matchesSearch && matchesTech && matchesAcademic;
    });
  };

  const filteredProjects = getFilteredProjects();

  // Pagination Calculations
  const totalPages = Math.ceil(filteredProjects.length / projectsPerPage);
  const indexOfLastProject = currentPage * projectsPerPage;
  const indexOfFirstProject = indexOfLastProject - projectsPerPage;
  const currentProjects = filteredProjects.slice(indexOfFirstProject, indexOfLastProject);

  return (
    <div className={`admin-projects-container${showForm ? ' has-form' : ''}`}>
      {/* FORM SECTION */}
      {showForm ? (
        <div className="form-wrapper">
          <ProjectForm
            onSubmit={handleFormSubmit}
            editingProject={editingProject}
            onCancel={handleCancelForm}
          />
        </div>
      ) : (
        /* PROJECTS LIST SECTION */
        <div className="projects-list-section">
          <div className="section-header">
            <div>
              <h2>📊 Software Projects</h2>
              <p>Manage software projects listings</p>
            </div>
            <button
              className="btn btn-add-project"
              onClick={() => {
                setEditingProject(null);
                setShowForm(true);
              }}
            >
              ➕ Add Project
            </button>
          </div>

          {error && <div className="alert alert-error">{error}</div>}

          {/* ===== SEARCH & FILTER CARD ===== */}
          <div className="admin-filters-card">
            <h4>🔍 Search & Filter Projects</h4>
            <div className="admin-filters-grid">
              <input
                type="text"
                placeholder="Search by title..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="search-input"
              />
              <select
                value={techDomain}
                onChange={(e) => setTechDomain(e.target.value)}
              >
                <option value="">All Technology Domains</option>
                {techDomainsList.map((domain) => (
                  <option key={domain} value={domain}>{domain}</option>
                ))}
              </select>
              <select
                value={academicDomain}
                onChange={(e) => setAcademicDomain(e.target.value)}
              >
                <option value="">All Academic Domains</option>
                {academicDomainsList.map((domain) => (
                  <option key={domain} value={domain}>{domain}</option>
                ))}
              </select>
              <button
                className="btn-clear"
                onClick={() => {
                  setSearch('');
                  setTechDomain('');
                  setAcademicDomain('');
                }}
              >
                Reset Filters
              </button>
            </div>
          </div>

          {/* ===== BULK PRICE UPDATE CARD ===== */}
          <div className="admin-bulk-card">
            <h4>⚡ Bulk Price Update by Technology Domain</h4>
            <form onSubmit={handleBulkPriceUpdate} className="bulk-form-grid">
              <select
                value={bulkTechDomain}
                onChange={(e) => setBulkTechDomain(e.target.value)}
                required
              >
                <option value="">Select Technology Domain</option>
                {techDomainsList.map((domain) => (
                  <option key={domain} value={domain}>{domain}</option>
                ))}
              </select>
              <input
                type="number"
                placeholder="Original Price (₹)"
                value={bulkOriginalPrice}
                onChange={(e) => setBulkOriginalPrice(e.target.value)}
                required
                min="0"
              />
              <label className="bulk-checkbox-label">
                <input
                  type="checkbox"
                  checked={bulkIsDiscountActive}
                  onChange={(e) => setBulkIsDiscountActive(e.target.checked)}
                />
                Enable Discount
              </label>
              {bulkIsDiscountActive && (
                <input
                  type="number"
                  placeholder="Discounted Price (₹)"
                  value={bulkDiscountedPrice}
                  onChange={(e) => setBulkDiscountedPrice(e.target.value)}
                  required={bulkIsDiscountActive}
                  min="0"
                />
              )}
              <button type="submit" className="btn-bulk" disabled={bulkLoading}>
                {bulkLoading ? 'Updating...' : 'Apply Bulk Price'}
              </button>
            </form>
            {bulkSuccess && <div className="bulk-success-message">{bulkSuccess}</div>}
          </div>

          {/* ===== STATS ROW ===== */}
          <div className="projects-grid-container">


            {/* PROJECTS TABLE */}
            {loading && projects.length === 0 ? (
              <div className="loading">Loading projects...</div>
            ) : filteredProjects.length === 0 ? (
              <div className="empty-state">
                <p>No software projects found matching filters.</p>
              </div>
            ) : (
              <div className="mba-table-card">
                <div className="mba-table">
                  <div className="mba-table-head">
                    <span>SR NO</span>
                    <span>Title</span>
                    <span>Academic Domain</span>
                    <span>Technology Domain</span>
                  </div>
                  {currentProjects.map((project, index) => (
                    <div key={project.id} className="mba-table-row">
                      <div>{(currentPage - 1) * projectsPerPage + index + 1}</div>
                      <div className="title-col">
                        <strong>{project.title}</strong>
                      </div>
                      <div>{project.domain || '-'}</div>
                      <div>{project.category || '-'}</div>
                      <div className="action-col">
                        <button onClick={() => handleEdit(project)}>Edit</button>
                        <button className="danger" onClick={() => handleDelete(project.id)}>Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* PAGINATION CONTROLS */}
            {!loading && totalPages > 1 && (
              <footer className="mba-pagination">
                <button
                  className="page-btn"
                  onClick={() => {
                    setCurrentPage(prev => Math.max(prev - 1, 1));
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  disabled={currentPage === 1}
                >
                  <FaArrowLeft /> Previous
                </button>
                <div className="page-info">
                  Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                </div>
                <button
                  className="page-btn"
                  onClick={() => {
                    setCurrentPage(prev => Math.min(prev + 1, totalPages));
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  disabled={currentPage === totalPages}
                >
                  Next <FaArrowRight />
                </button>
              </footer>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProjects;
